import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { cityImage } from "@/lib/cityImages";
import ProviderCard, { startingPrice } from "@/components/ProviderCard";
import ProviderProfileModal from "@/components/ProviderProfileModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  MapPin, Building2, Stethoscope, TrendingDown, Loader2, Plane, Clock, CalendarDays,
  Sparkles, ArrowRight,
} from "lucide-react";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString()}`);

export default function CityDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [specialty, setSpecialty] = useState("all");
  const [accreditation, setAccreditation] = useState("all");
  const [sortBy, setSortBy] = useState("rating");

  useEffect(() => {
    setData(null); setNotFound(false); setSpecialty("all");
    api.get(`/v1/cities/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (data) {
      document.title = `${data.name} Hospitals & Medical Tourism | SarvaSeva`;
      let tag = document.querySelector('meta[name="description"]');
      if (!tag) { tag = document.createElement("meta"); tag.name = "description"; document.head.appendChild(tag); }
      tag.setAttribute("content", `${data.stats.hospitals} accredited hospitals in ${data.name}, ${data.state} across ${data.stats.specialties} specialties. ${data.intro}`.slice(0, 160));
    }
  }, [data]);

  const specialties = useMemo(
    () => Array.from(new Set((data?.providers || []).flatMap((p) => p.specialties || []))).sort(),
    [data]);

  const filtered = useMemo(() => {
    let list = (data?.providers || []).filter((p) => {
      if (specialty !== "all" && !(p.specialties || []).includes(specialty)) return false;
      if (accreditation !== "all" && !(p.accreditation || []).includes(accreditation)) return false;
      return true;
    });
    const cmp = {
      rating: (a, b) => (b.rating || 0) - (a.rating || 0),
      "price-asc": (a, b) => (startingPrice(a) ?? Infinity) - (startingPrice(b) ?? Infinity),
      "price-desc": (a, b) => (startingPrice(b) ?? -Infinity) - (startingPrice(a) ?? -Infinity),
      name: (a, b) => a.name.localeCompare(b.name),
    }[sortBy];
    return [...list].sort((a, b) => (Number(b.featured) - Number(a.featured)) || cmp(a, b));
  }, [data, specialty, accreditation, sortBy]);

  if (notFound) return (
    <div className="min-h-screen grid place-items-center bg-[#F7F9FC] text-center px-4" data-testid="city-notfound">
      <div>
        <Building2 size={40} className="mx-auto text-[#98A2B3]" />
        <h1 className="font-heading font-bold text-xl text-[#101828] mt-4">City not found</h1>
        <Button className="mt-5 rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E]" onClick={() => navigate("/cities")}>Browse all cities</Button>
      </div>
    </div>
  );

  if (!data) return <div className="min-h-screen grid place-items-center bg-[#F7F9FC] text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  const t = data.travel || {};

  return (
    <div className="bg-[#F7F9FC] min-h-screen" data-testid="city-detail-page">
      {/* Hero */}
      <section className="relative text-white overflow-hidden">
        <img src={cityImage(data.name)} alt={data.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D5C75]/95 via-[#0D5C75]/80 to-[#2A9D8F]/55" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <Link to="/cities" className="text-white/80 hover:text-white text-sm inline-flex items-center gap-1 mb-3" data-testid="back-to-cities">← All Cities</Link>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight" data-testid="city-name">{data.name}</h1>
          <p className="mt-2 text-white/85 text-lg flex items-center gap-1.5"><MapPin size={18} /> {data.state}</p>
          <div className="mt-6 flex flex-wrap gap-3" data-testid="city-stats-bar">
            <Stat icon={Building2} label="Hospitals" value={data.stats.hospitals} />
            <Stat icon={Stethoscope} label="Specialties" value={data.stats.specialties} />
            {data.stats.savings_pct != null && <Stat icon={TrendingDown} label="Savings" value={`Up to ${data.stats.savings_pct}%`} />}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 space-y-14">
        {/* Intro */}
        <section>
          <span className="section-overline mb-3" />
          <h2 className="font-heading text-2xl font-extrabold text-[#101828]">Why {data.name} for Medical Tourism?</h2>
          <p className="mt-3 text-[#475467] leading-relaxed max-w-3xl" data-testid="city-intro">{data.intro}</p>
        </section>

        {/* Top specialties scroller */}
        {data.top_specialties?.length > 0 && (
          <section>
            <h2 className="font-heading text-xl font-bold text-[#101828] mb-4">Top Specialties in {data.name}</h2>
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2" data-testid="city-specialty-scroller">
              {data.top_specialties.map((s) => (
                <button key={s.name} onClick={() => setSpecialty(specialty === s.name ? "all" : s.name)}
                  data-testid={`city-specialty-${s.name}`}
                  className={`shrink-0 w-56 text-left rounded-2xl border p-4 transition-all ${specialty === s.name ? "border-[#0D5C75] bg-[#E9F5F9] ring-1 ring-[#0D5C75]" : "border-[#E4E7EC] bg-white hover:shadow-md"}`}>
                  <div className="h-9 w-9 rounded-xl grid place-items-center bg-[#0D5C75]/10 text-[#0D5C75] mb-2"><Stethoscope size={18} /></div>
                  <div className="font-semibold text-sm text-[#101828] leading-tight">{s.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{s.provider_count} hospital{s.provider_count === 1 ? "" : "s"}</div>
                  {s.avg_savings_pct != null && <div className="text-xs text-[#10B981] font-semibold mt-1">~{s.avg_savings_pct}% savings</div>}
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Hospitals grid */}
        <section>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h2 className="font-heading text-xl font-bold text-[#101828]">Hospitals in {data.name}</h2>
            <div className="flex flex-wrap gap-2">
              <Select value={specialty} onValueChange={setSpecialty}>
                <SelectTrigger className="w-[180px] h-9 rounded-full" data-testid="city-filter-specialty"><SelectValue placeholder="Specialty" /></SelectTrigger>
                <SelectContent className="max-h-[300px]"><SelectItem value="all">All Specialties</SelectItem>
                  {specialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={accreditation} onValueChange={setAccreditation}>
                <SelectTrigger className="w-[150px] h-9 rounded-full" data-testid="city-filter-accreditation"><SelectValue placeholder="Accreditation" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="JCI">JCI</SelectItem><SelectItem value="NABH">NABH</SelectItem></SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[150px] h-9 rounded-full" data-testid="city-filter-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rating">Top Rated</SelectItem>
                  <SelectItem value="price-asc">Price: Low to High</SelectItem>
                  <SelectItem value="price-desc">Price: High to Low</SelectItem>
                  <SelectItem value="name">Name (A–Z)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-5" data-testid="city-providers-count">Showing {filtered.length} hospital{filtered.length === 1 ? "" : "s"}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 items-stretch">
            {filtered.map((p, i) => (
              <ProviderCard key={p.id} p={p} i={i} onView={(x) => { setSelected(x); setOpen(true); }} />
            ))}
          </div>
        </section>

        {/* Savings table */}
        {data.savings_table?.length > 0 && (
          <section className="rounded-2xl bg-white border border-[#E4E7EC] p-6" data-testid="city-savings-table">
            <h2 className="font-heading text-xl font-bold text-[#101828] mb-1">Average Savings in {data.name}</h2>
            <p className="text-sm text-muted-foreground mb-4">Estimated procedure costs vs. typical US prices, averaged across {data.name} hospitals.</p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Procedure</TableHead>
                  <TableHead className="text-right">India (avg)</TableHead>
                  <TableHead className="text-right">USA (typical)</TableHead>
                  <TableHead className="text-right">You Save</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.savings_table.map((r) => (
                    <TableRow key={r.procedure} data-testid={`savings-row-${r.procedure}`}>
                      <TableCell className="font-medium">{r.procedure}</TableCell>
                      <TableCell className="text-right font-semibold text-[#0D5C75]">{money(r.india_price)}</TableCell>
                      <TableCell className="text-right text-muted-foreground line-through">{money(r.usa_price)}</TableCell>
                      <TableCell className="text-right"><span className="text-[#10B981] font-bold">{r.savings_pct}%</span></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>
        )}

        {/* Travel info */}
        <section>
          <h2 className="font-heading text-xl font-bold text-[#101828] mb-4">How to Get to {data.name}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="city-travel-info">
            <TravelCard icon={Plane} label="Nearest Airport" value={t.airport} />
            <TravelCard icon={Clock} label="From New York / London" value={`${t.from_new_york} / ${t.from_london}`} />
            <TravelCard icon={Clock} label="From Dubai" value={t.from_dubai} />
            <TravelCard icon={CalendarDays} label="Best Time to Visit" value={t.best_time} />
          </div>
        </section>

        {/* CTA */}
        <section className="tricolor-top relative overflow-hidden rounded-3xl mv-gradient-hero text-white p-10 text-center" data-testid="city-cta">
          <Sparkles size={28} className="mx-auto text-white/90" />
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold mt-3">Ready to explore {data.name} hospitals?</h2>
          <p className="mt-2 text-white/85 max-w-xl mx-auto">Use our AI Matchmaker to find the best hospital in {data.name} for your specific needs.</p>
          <Button className="mt-6 rounded-full bg-[#D97706] text-white hover:bg-[#b45f05] hover:text-white h-12 px-8 text-base"
            onClick={() => navigate("/assistant")} data-testid="city-find-hospital-btn">
            Find My Hospital <ArrowRight size={18} className="ml-1.5" />
          </Button>
        </section>
      </div>

      <ProviderProfileModal provider={selected} open={open} onOpenChange={setOpen} />
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-black/25 backdrop-blur px-4 py-3 flex items-center gap-3">
      <Icon size={22} className="text-white/90" />
      <div><div className="font-heading font-extrabold text-xl leading-none">{value}</div>
        <div className="text-[11px] uppercase tracking-wide text-white/75 mt-0.5">{label}</div></div>
    </div>
  );
}

function TravelCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E4E7EC] p-4">
      <div className="h-9 w-9 rounded-xl grid place-items-center bg-[#0D5C75]/10 text-[#0D5C75] mb-2"><Icon size={18} /></div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</div>
      <div className="text-sm font-medium text-[#101828] mt-0.5">{value}</div>
    </div>
  );
}
